import "./styles.css";
import { router, type Route } from "./router.ts";
import { store } from "./store.ts";
import { renderBookshelf } from "./views/bookshelf.ts";
import { renderViewer } from "./views/viewer.ts";
import { renderEditor } from "./views/editor.ts";
import { renderSettings } from "./views/settings.ts";

const app = document.getElementById("app");
if (!app) throw new Error("Missing #app root");
const appEl: HTMLElement = app;

let cleanup: (() => void) | null = null;
let renderGeneration = 0;
let activeController: AbortController | null = null;

async function renderRoute(route: Route): Promise<void> {
  const generation = ++renderGeneration;
  activeController?.abort();
  const controller = new AbortController();
  activeController = controller;
  cleanup?.();
  cleanup = null;
  appEl.replaceChildren();
  appEl.className = route.name === "shelf" ? "app-room" : "app-overlay";

  if (generation !== renderGeneration) {
    controller.abort();
    return;
  }

  const attach = (dispose: () => void) => {
    if (generation !== renderGeneration) {
      controller.abort();
      dispose();
      return;
    }
    cleanup = () => {
      controller.abort();
      dispose();
    };
  };

  switch (route.name) {
    case "shelf":
      attach(renderBookshelf(appEl));
      break;
    case "viewer":
      attach(await renderViewer(appEl, route.albumId, route.page, controller.signal));
      break;
    case "editor":
      attach(await renderEditor(appEl, route.albumId, route.page, controller.signal));
      break;
    case "settings":
      attach(await renderSettings(appEl, controller.signal));
      break;
  }

  if (generation !== renderGeneration) controller.abort();
}

async function bootstrap(): Promise<void> {
  await store.init();
  router.subscribe((route) => void renderRoute(route));
  await renderRoute(router.current);
  registerServiceWorker();
}

function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  void navigator.serviceWorker.register("/sw.js").then((registration) => {
    registration.addEventListener("updatefound", () => {
      const worker = registration.installing;
      if (!worker) return;
      worker.addEventListener("statechange", () => {
        if (worker.state === "installed" && navigator.serviceWorker.controller) {
          showUpdateBanner(() => {
            worker.postMessage({ type: "SKIP_WAITING" });
            window.location.reload();
          });
        }
      });
    });
  });
}

function showUpdateBanner(onUpdate: () => void): void {
  const banner = document.createElement("div");
  banner.className = "update-banner";
  const label = document.createElement("span");
  label.textContent = "Update available";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn btn-primary";
  btn.textContent = "Reload";
  btn.addEventListener("click", onUpdate);
  banner.append(label, btn);
  document.body.appendChild(banner);
}

void bootstrap();
